import { cx } from "@emotion/css";
import AccessTimeIcon from "@mui/icons-material/AccessTime";
import useDeviceType from "hooks/useDeviceType";
import useOpenClose from "hooks/useOpenClose";
import PrimaryButton from "library/Atoms/Button/PrimaryButton";
import Chip from "library/Atoms/Chip";
import MiscDetailsLayout from "library/Layouts/DetailsLayout/MiscDetailsLayout/MiscDetailsLayout";
import { isUndefined } from "lodash";
import OrderBriefInfo from "pages/Dashboard/components/OrdersList/OrderItem/OrderBriefInfo/OrderBriefInfo";
import useOrders from "pages/Dashboard/hooks/useOrders";
import OrderDetailsLayout from "pages/Dashboard/layouts/OrderDetailsLayout/OrderDetailsLayout";
import ConfirmCancelOrderModal from "pages/Dashboard/modals/ConfirmCancelOrderModal/ConfirmCancelOrderModal";
import PaymentModal from "pages/Dashboard/modals/PaymentModal/PaymentModal";
import { PropTypes } from "prop-types";
import { useEffect, useMemo, useRef } from "react";
import { Trans, useTranslation } from "react-i18next";
import { useDispatch, useSelector } from "react-redux";
import { retrieveContactPaymentType } from "store/features/contact.store";
import { retrieveContactTimezone } from "store/features/localization.store";
import { fetchOrderPositionRequest } from "store/requests/order.requests";
import { formatFullDate } from "utility/date-fns";
import { convertTimestampToDate, formatPrice } from "utility/helpers/general";
import { calculatePackagesAmount, getPriceWithVat } from "utility/helpers/orders";
import { CLAIMED_STATUS, FAILED_PAYMENT_STATUS, PENDING_PAYMENT_STATUS } from "../../../../constants";
import CancelOrderBanner from "./CancelOrderBanner/CancelOrderBanner";
import DropDetails from "./DropDetails/DropDetails";
import styles from "./OrderDetails.styles";
import PaymentBanner from "./PaymentBanner/PaymentBanner";
import PaymentForm from "./PaymentForm/PaymentForm";
import SaveOrderButton from "./SaveOrderButton/SaveOrderButton";
import StatusBanner from "./StatusBanner/StatusBanner";

const OrderDetails = ({ mapOpen, onClose }) => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const { isMobile } = useDeviceType();
  const intervalRef = useRef();

  const timezone = useSelector(retrieveContactTimezone);
  const { isCheckoutPaymentType } = useSelector(retrieveContactPaymentType);

  const [{ selectedOrder }, , { isOrderSelected }] = useOrders();
  const {
    totalPrice,
    startingPrice,
    discount: { discount },
    currency,
  } = useMemo(() => {
    return getPriceWithVat(selectedOrder.price, isCheckoutPaymentType);
  }, [isCheckoutPaymentType, selectedOrder.price]);

  const [isCancelOrderModalOpen, openCancelOrderModal, closeCancelOrderModal] = useOpenClose(false);
  const [isPaymentModalOpen, openPaymentModal, closePaymentModal] = useOpenClose(false);

  const shouldShowDiscount = useMemo(() => {
    return isOrderSelected ? discount > 0 : false;
  }, [isOrderSelected, discount]);

  useEffect(() => {
    if (!isOrderSelected || selectedOrder.status !== CLAIMED_STATUS) return;

    // if not testing env
    if (isUndefined(window.Cypress)) {
      intervalRef.current = setInterval(() => dispatch(fetchOrderPositionRequest(selectedOrder.id)), 60000);
    }

    dispatch(fetchOrderPositionRequest(selectedOrder.id));
  }, [dispatch, selectedOrder.id, selectedOrder.status, isOrderSelected]);

  useEffect(() => {
    if (!isOrderSelected || selectedOrder.status !== CLAIMED_STATUS) clearInterval(intervalRef.current);
  }, [isOrderSelected, selectedOrder.status]);

  useEffect(() => {
    return () => intervalRef.current && clearInterval(intervalRef.current);
  }, []);

  if (!isOrderSelected) return null;
  return (
    <OrderDetailsLayout
      statusBarSection={<StatusBanner id={selectedOrder.reference} status={selectedOrder.status} />}
      paymentSection={
        [FAILED_PAYMENT_STATUS, PENDING_PAYMENT_STATUS].includes(selectedOrder.paymentStatus) && (
          <PaymentBanner onStartPayment={openPaymentModal} />
        )
      }
      briefInfoSection={
        <>
          <OrderBriefInfo
            reference={selectedOrder.reference}
            startDate={selectedOrder.startDate}
            endDate={selectedOrder.endDate}
            packages={calculatePackagesAmount(selectedOrder.drops)}
            paymentStatus={selectedOrder.paymentStatus}
          />

          {selectedOrder.isExpress && (
            <Chip
              label={t("common.entities.order.express")}
              icon={<AccessTimeIcon />}
              data-cy={`express-${selectedOrder.reference}-order-details-chip`}
            />
          )}
        </>
      }
      extraInfoSection={
        <>
          <MiscDetailsLayout
            className="col-sm-4 col-6 my-3"
            titleSection={t("utils.timestamps.createdAt")}
            contentSection={formatFullDate(convertTimestampToDate(selectedOrder.created_at), timezone)}
          />

          <MiscDetailsLayout
            className="col-sm-4 col-6 my-3"
            titleSection={t("utils.timestamps.updatedAt")}
            contentSection={formatFullDate(convertTimestampToDate(selectedOrder.updated_at), timezone)}
          />

          <MiscDetailsLayout
            className="col-sm-4 col-6 my-3"
            titleSection={t("utils.timestamps.scheduledFor")}
            contentSection={formatFullDate(convertTimestampToDate(selectedOrder.delivery_date), timezone)}
          />
        </>
      }
      dropsListSection={selectedOrder.drops?.map(({ reference: dropRef, ...drop }) => (
        <DropDetails
          key={dropRef}
          pick_up={selectedOrder.pick_up}
          drop={{ ...drop, reference: dropRef, currency }}
          rider={selectedOrder.rider}
        />
      ))}
      priceSection={
        <>
          <span className="pr-2">{t("utils.pricing.totalPrice")}:</span>

          <Trans
            i18nKey={isCheckoutPaymentType ? "utils.pricing.inclVAT" : "utils.pricing.exclVAT"}
            context={shouldShowDiscount ? "discount" : ""}
            tOptions={{ price: formatPrice(totalPrice, currency), discount: formatPrice(startingPrice, currency) }}
            components={{
              span: <span data-cy={`order-${selectedOrder.reference}-price`} />,
              del: <del className="text-fs-12 text-color-error" />,
              span_discount: <span data-cy={`order-${selectedOrder.reference}-discount`} />,
            }}
            defaults={
              shouldShowDiscount
                ? `<span>{{price}}</span> <del>(<span_discount>{{discount}}</span_discount>)</del> ${
                    isCheckoutPaymentType ? "inc" : "excl"
                  }. VAT`
                : `<span>{{price}}</span> ${isCheckoutPaymentType ? "inc" : "excl"}. VAT`
            }
          />
        </>
      }
      controlsSection={
        <>
          {!isMobile && <SaveOrderButton className={styles.closeBtn(isMobile)} />}

          <PrimaryButton
            id="back-to-orders-btn"
            className={cx(styles.closeBtn(isMobile), "w-100")}
            contentClassName="text-truncate text-fs-12"
            label={t("common.buttons.backToBtn", { context: mapOpen && isMobile ? "map" : "orders" })}
            onClick={onClose}
            data-cy={mapOpen && isMobile ? "back-to-map" : "back-to-orders"}
          />
        </>
      }
      cancelOrderSection={<CancelOrderBanner onStartCancelling={openCancelOrderModal} />}
      modals={
        <>
          {isCancelOrderModalOpen && <ConfirmCancelOrderModal onClose={closeCancelOrderModal} />}

          {isPaymentModalOpen && (
            <PaymentModal onClose={closePaymentModal}>
              <PaymentForm onClose={closePaymentModal} secret={selectedOrder.payment?.secret} />
            </PaymentModal>
          )}
        </>
      }
    />
  );
};

OrderDetails.propTypes = {
  mapOpen: PropTypes.bool.isRequired,
  onClose: PropTypes.func.isRequired,
};

export default OrderDetails;
